import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Profile, Band, Event } from '../types/database';

const PAGE_SIZE = 50;

interface AdminStats {
  totalUsers: number;
  totalBands: number;
  totalEvents: number;
  totalPublicEvents: number;
}

interface PaginationState {
  page: number;
  totalCount: number;
  totalPages: number;
}

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats>({ totalUsers: 0, totalBands: 0, totalEvents: 0, totalPublicEvents: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const [users, bands, events, publicEvents] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('bands').select('id', { count: 'exact', head: true }),
        supabase.from('events').select('id', { count: 'exact', head: true }),
        supabase.from('events').select('id', { count: 'exact', head: true }).eq('visibility', 'public'),
      ]);
      setStats({
        totalUsers: users.count ?? 0,
        totalBands: bands.count ?? 0,
        totalEvents: events.count ?? 0,
        totalPublicEvents: publicEvents.count ?? 0,
      });
      setLoading(false);
    }
    fetch();
  }, []);

  return { stats, loading };
}

export function useAdminUsers() {
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationState>({ page: 0, totalCount: 0, totalPages: 0 });

  const fetchUsers = useCallback(async (search?: string, page = 0) => {
    setLoading(true);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase.from('profiles').select('*', { count: 'exact' }).order('created_at', { ascending: false });
    if (search?.trim()) {
      query = query.or(`email.ilike.%${search}%,display_name.ilike.%${search}%`);
    }
    const { data, count } = await query.range(from, to);
    const totalCount = count ?? 0;
    setUsers(data ?? []);
    setPagination({ page, totalCount, totalPages: Math.ceil(totalCount / PAGE_SIZE) });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { users, loading, pagination, fetchUsers };
}

export function useAdminBands() {
  const [bands, setBands] = useState<Band[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationState>({ page: 0, totalCount: 0, totalPages: 0 });

  const fetchBands = useCallback(async (search?: string, page = 0) => {
    setLoading(true);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase.from('bands').select('*', { count: 'exact' }).order('created_at', { ascending: false });
    if (search?.trim()) {
      query = query.or(`name.ilike.%${search}%,location_name.ilike.%${search}%,slug.ilike.%${search}%`);
    }
    const { data, count } = await query.range(from, to);
    const totalCount = count ?? 0;
    setBands(data ?? []);
    setPagination({ page, totalCount, totalPages: Math.ceil(totalCount / PAGE_SIZE) });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBands();
  }, [fetchBands]);

  return { bands, loading, pagination, fetchBands };
}

export function useAdminEvents() {
  const [events, setEvents] = useState<(Event & { band?: { name: string; slug: string } })[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationState>({ page: 0, totalCount: 0, totalPages: 0 });

  const fetchEvents = useCallback(async (search?: string, page = 0) => {
    setLoading(true);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    const trimmed = search?.trim();

    // If searching, find matching band IDs first so we can search by band name too
    let matchingBandIds: string[] = [];
    if (trimmed) {
      const { data: matchingBands } = await supabase
        .from('bands')
        .select('id')
        .ilike('name', `%${trimmed}%`);
      matchingBandIds = (matchingBands ?? []).map((b) => b.id);
    }

    let query = supabase
      .from('events')
      .select('*, band:bands(name, slug)', { count: 'exact' })
      .order('start_time', { ascending: false });

    if (trimmed) {
      if (matchingBandIds.length > 0) {
        query = query.or(`title.ilike.%${trimmed}%,venue_name.ilike.%${trimmed}%,band_id.in.(${matchingBandIds.join(',')})`);
      } else {
        query = query.or(`title.ilike.%${trimmed}%,venue_name.ilike.%${trimmed}%`);
      }
    }

    const { data, count } = await query.range(from, to);
    const totalCount = count ?? 0;
    setEvents((data as typeof events) ?? []);
    setPagination({ page, totalCount, totalPages: Math.ceil(totalCount / PAGE_SIZE) });
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return { events, loading, pagination, fetchEvents };
}

export async function adminUpdateProfile(
  userId: string,
  updates: Partial<Pick<Profile, 'display_name' | 'email' | 'is_admin'>>
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('profiles').update(updates).eq('id', userId);
  return { error: error?.message ?? null };
}

export async function adminResetPassword(email: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/login`,
  });
  return { error: error?.message ?? null };
}

export async function adminDeleteBand(bandId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('bands').delete().eq('id', bandId);
  return { error: error?.message ?? null };
}

export async function adminUpdateBand(
  bandId: string,
  updates: Partial<Pick<Band, 'name' | 'is_public' | 'description' | 'location_name' | 'location_country'>>
): Promise<{ error: string | null }> {
  const { error } = await supabase.from('bands').update(updates).eq('id', bandId);
  return { error: error?.message ?? null };
}

export async function adminDeleteEvent(eventId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.from('events').delete().eq('id', eventId);
  return { error: error?.message ?? null };
}
